import { useForm, useFieldArray } from "react-hook-form";

import type { QuestionnaireFormValues } from "../types";

export const INITIAL_FORM_VALUES: QuestionnaireFormValues = {
  title: "",
  description: "",
  usageType: "triage",
  questions: [
    {
      id: "q-1",
      label: "",
      type: "shortText",
      isRequired: false,
      helperText: "",
      optionsCsv: "",
      allowMultipleAnswers: false,
      isPersistent: true,
    },
  ],
};

export interface UseQuestionnaireFormDataResult {
  control: ReturnType<typeof useForm<QuestionnaireFormValues>>["control"];
  register: ReturnType<typeof useForm<QuestionnaireFormValues>>["register"];
  watch: ReturnType<typeof useForm<QuestionnaireFormValues>>["watch"];
  reset: ReturnType<typeof useForm<QuestionnaireFormValues>>["reset"];
  getValues: ReturnType<typeof useForm<QuestionnaireFormValues>>["getValues"];
  setValue: ReturnType<typeof useForm<QuestionnaireFormValues>>["setValue"];
  setFocus: ReturnType<typeof useForm<QuestionnaireFormValues>>["setFocus"];
  trigger: ReturnType<typeof useForm<QuestionnaireFormValues>>["trigger"];
  handleSubmit: ReturnType<
    typeof useForm<QuestionnaireFormValues>
  >["handleSubmit"];
  formIsDirty: boolean;
  questionFields: ReturnType<
    typeof useFieldArray<QuestionnaireFormValues>
  >["fields"];
  watchedValues: QuestionnaireFormValues;
  append: ReturnType<typeof useFieldArray<QuestionnaireFormValues>>["append"];
  move: ReturnType<typeof useFieldArray<QuestionnaireFormValues>>["move"];
  update: ReturnType<typeof useFieldArray<QuestionnaireFormValues>>["update"];
  remove: ReturnType<typeof useFieldArray<QuestionnaireFormValues>>["remove"];
  insert: ReturnType<typeof useFieldArray<QuestionnaireFormValues>>["insert"];
}

/**
 * Hook de dados do formulário de questionário.
 *
 * Responsável por: useForm + useFieldArray, control, register, watch, reset,
 * formState.isDirty, questionFields, watchedValues, append, move, update.
 *
 * Tipos em ../types.ts.
 */
export function useQuestionnaireFormData(): UseQuestionnaireFormDataResult {
  const {
    control,
    register,
    watch,
    reset,
    getValues,
    setValue,
    setFocus,
    trigger,
    handleSubmit,
    formState: { isDirty },
  } = useForm<QuestionnaireFormValues>({
    defaultValues: INITIAL_FORM_VALUES,
  });

  const {
    fields: questionFields,
    append,
    move,
    update,
    remove,
    insert,
  } = useFieldArray({
    control,
    name: "questions",
  });

  const watchedValues = watch();

  return {
    control,
    register,
    watch,
    reset,
    getValues,
    setValue,
    setFocus,
    trigger,
    handleSubmit,
    formIsDirty: isDirty,
    questionFields,
    watchedValues,
    append,
    move,
    update,
    remove,
    insert,
  };
}
